import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Check, ChevronDown, Clock3, Code2, Cpu, Eye, Lightbulb, LoaderCircle, LockKeyhole, Play, RotateCcw, Save, Send, Sparkles, Target, Terminal, Workflow, X } from 'lucide-react';
import type { CodeLanguage, GuidedQuestion as Question } from '../content/types';
import { api, type ExecutionResult } from '../lib/api';

const LANGUAGES: CodeLanguage[] = ['Java', 'Python', 'JavaScript'];

export function GuidedQuestion({ question, day }: { question: Question; day: number }) {
  const [language, setLanguage] = useState<CodeLanguage>(() => {
    const stored = localStorage.getItem('iprep_code_language') as CodeLanguage | null;
    return stored && LANGUAGES.includes(stored) ? stored : 'Java';
  });
  const storageKey = `iprep_question_draft_${day}_${language}`;
  const [revealedHints, setRevealedHints] = useState(0);
  const [showApproach, setShowApproach] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  const [activeCase, setActiveCase] = useState(0);
  const [runningCase, setRunningCase] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<Record<number, ExecutionResult>>({});

  useEffect(() => {
    setRevealedHints(0);
    setShowApproach(false);
    setWorkspaceOpen(false);
    setActiveCase(0);
    setResults({});
  }, [day]);

  useEffect(() => {
    setDraft(localStorage.getItem(storageKey) ?? question.starterCode?.[language] ?? '');
    setSaved(false);
    setResults({});
  }, [storageKey, language, question.starterCode]);

  function chooseLanguage(next: CodeLanguage) {
    localStorage.setItem(storageKey, draft);
    localStorage.setItem('iprep_code_language', next);
    setLanguage(next);
  }

  function saveDraft() {
    localStorage.setItem(storageKey, draft);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1400);
  }

  function resetCode() {
    const starter = question.starterCode?.[language] ?? '';
    setDraft(starter);
    localStorage.setItem(storageKey, starter);
    setResults({});
  }

  async function executeCase(index: number) {
    const test = question.testCases?.[index];
    if (!test) return failure('Test case not found.');
    try {
      return await api.execute({ language, sourceCode: draft, stdin: test.input, expectedOutput: test.expected });
    } catch (error) {
      return failure(error instanceof Error ? error.message : 'The runner could not execute this submission.');
    }
  }

  async function runSelected() {
    saveDraft();
    setRunningCase(activeCase);
    const result = await executeCase(activeCase);
    setResults((current) => ({ ...current, [activeCase]: result }));
    setRunningCase(null);
  }

  async function submitAll() {
    if (!question.testCases) return;
    saveDraft();
    setSubmitting(true);
    setResults({});
    for (let index = 0; index < question.testCases.length; index++) {
      setRunningCase(index);
      const result = await executeCase(index);
      setResults((current) => ({ ...current, [index]: result }));
      if (result.compileOutput || result.status === 'Runner unavailable') break;
    }
    setRunningCase(null);
    setSubmitting(false);
  }

  const KindIcon = question.kind === 'coding' ? Code2 : question.kind === 'design' ? Workflow : Sparkles;
  const tests = question.testCases ?? [];
  const activeTest = tests[activeCase];
  const activeResult = results[activeCase];
  const passedCount = Object.values(results).filter((result) => result.passed).length;

  return (
    <div className={`guided-question ${question.kind}`}>
      <header className="question-header">
        <div className="question-kind"><KindIcon /><span><small>BUILT-IN {question.kind.toUpperCase()} CHALLENGE</small><b>{question.title}</b></span></div>
        <div className="self-contained"><Check /> Everything you need is here</div>
      </header>

      <div className="question-main">
        <section className="question-statement">
          <span className="question-label">THE QUESTION</span>
          <h3>{question.prompt}</h3>
          <p>{question.description}</p>
        </section>
        <section className="question-example">
          <div><small>{question.example.inputLabel}</small><pre>{question.example.input}</pre></div>
          <div className="example-arrow">↓</div>
          <div><small>{question.example.outputLabel}</small><pre className="output">{question.example.output}</pre></div>
          <p>{question.example.explanation}</p>
        </section>
      </div>

      <section className="question-constraints">
        <span className="question-label">{question.kind === 'coding' ? 'CONSTRAINTS' : question.kind === 'design' ? 'YOUR ANSWER MUST COVER' : 'ANSWER STANDARD'}</span>
        <div>{question.constraints.map((constraint) => <p key={constraint}><Check /> {constraint}</p>)}</div>
      </section>

      <section className="hint-ladder">
        <div className="question-label-row"><span className="question-label">PROGRESSIVE HINTS</span><small>Reveal only when you are genuinely stuck</small></div>
        <div>{question.hints.map((hint, index) => {
          const visible = index < revealedHints;
          const available = index === revealedHints;
          return <motion.button key={hint} className={visible ? 'revealed' : ''} onClick={() => available && setRevealedHints(index + 1)} disabled={!available && !visible} layout><span>{visible ? <Lightbulb /> : <LockKeyhole />}</span><div><small>HINT {index + 1}</small><p>{visible ? hint : available ? 'Reveal this hint' : 'Unlock the previous hint first'}</p></div>{available && <ChevronDown />}</motion.button>;
        })}</div>
      </section>

      <section className="answer-workspace">
        <button className="workspace-toggle" onClick={() => setWorkspaceOpen(!workspaceOpen)}><span><Target /><div><small>{question.kind === 'coding' ? 'CODE, RUN & SUBMIT' : 'YOUR WORKSPACE'}</small><b>{question.kind === 'coding' ? 'Open the code editor and test runner' : draft ? 'Continue your answer' : 'Build your answer here'}</b></div></span><ChevronDown className={workspaceOpen ? 'open' : ''} /></button>
        {workspaceOpen && <motion.div className={`workspace-body ${question.kind === 'coding' ? 'coding-workspace' : ''}`} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
          {question.kind === 'coding' ? <>
            <div className="editor-toolbar">
              <div className="editor-file"><Terminal /><span><small>SOLUTION</small><b>{language === 'Java' ? 'Main.java' : language === 'Python' ? 'main.py' : 'main.js'}</b></span></div>
              <div className="editor-actions">
                <label><Code2 /><select value={language} onChange={(event) => chooseLanguage(event.target.value as CodeLanguage)}>{LANGUAGES.map((item) => <option key={item}>{item}</option>)}</select></label>
                <button onClick={resetCode} title="Reset starter code"><RotateCcw /></button>
                <button onClick={saveDraft}>{saved ? <Check /> : <Save />}</button>
              </div>
            </div>
            <div className="code-editor-shell"><div className="editor-gutter" aria-hidden="true">{draft.split('\n').map((_, index) => <span key={index}>{index + 1}</span>)}</div><textarea value={draft} onChange={(event) => { setDraft(event.target.value); setSaved(false); }} spellCheck={false} aria-label="Code editor" /></div>
            <div className="test-runner">
              <header><div><span className="question-label">TEST CASES</span><small>Your stdout must match the expected output</small></div>{Object.keys(results).length > 0 && <div className={passedCount === tests.length ? 'all-passed' : ''}><Check /> {passedCount}/{tests.length} passed</div>}</header>
              <div className="test-tabs">{tests.map((test, index) => {
                const result = results[index];
                return <button key={test.label} className={`${activeCase === index ? 'active' : ''} ${result ? result.passed ? 'passed' : 'failed' : ''}`} onClick={() => setActiveCase(index)}><span>{runningCase === index ? <LoaderCircle className="spin" /> : result ? result.passed ? <Check /> : <X /> : index + 1}</span>{test.label}</button>;
              })}</div>
              {activeTest && <div className="test-detail">
                <div><small>STANDARD INPUT</small><pre>{activeTest.input || '(empty input)'}</pre></div>
                <div><small>EXPECTED OUTPUT</small><pre>{activeTest.expected || '(empty output)'}</pre></div>
                {activeResult && <div className={`judge-output ${activeResult.passed ? 'passed' : 'failed'}`}><header><span>{activeResult.passed ? <Check /> : <X />}{activeResult.status}</span><div>{activeResult.time && <small><Clock3 /> {activeResult.time}s</small>}{activeResult.memory && <small><Cpu /> {Math.round(activeResult.memory / 1024)} MB</small>}</div></header><pre>{activeResult.compileOutput || activeResult.stderr || activeResult.message || activeResult.stdout || '(no output)'}</pre>{!activeResult.passed && activeResult.stdout && !activeResult.compileOutput && !activeResult.stderr && <p><b>Your output:</b> {activeResult.stdout.trim() || '(empty)'}</p>}</div>}
              </div>}
              <footer><button className="button ghost compact" onClick={runSelected} disabled={runningCase !== null || submitting}>{runningCase === activeCase ? <LoaderCircle className="spin" /> : <Play fill="currentColor" />}Run selected test</button><button className="button primary compact" onClick={submitAll} disabled={runningCase !== null || submitting}>{submitting ? <LoaderCircle className="spin" /> : <Send />}Submit all tests</button></footer>
            </div>
          </> : <>
            <textarea value={draft} onChange={(event) => { setDraft(event.target.value); setSaved(false); }} spellCheck placeholder={question.kind === 'design' ? 'Capture requirements, estimates, APIs, data model, flow, and tradeoffs…' : 'Draft your complete spoken answer…'} />
            <footer><small>Saved locally in this browser</small><button className="button ghost compact" onClick={saveDraft}>{saved ? <Check /> : <Save />}{saved ? 'Saved' : 'Save draft'}</button></footer>
          </>}
        </motion.div>}
      </section>

      <section className="approach-reveal">
        <button onClick={() => setShowApproach(!showApproach)}>{showApproach ? <Eye /> : <LockKeyhole />}<span><small>GUIDED APPROACH</small><b>{showApproach ? 'Hide the reasoning path' : 'Reveal after attempting the question'}</b></span><ChevronDown className={showApproach ? 'open' : ''} /></button>
        {showApproach && <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>{question.approach}</motion.p>}
      </section>

      <section className="follow-up-drills">
        <div className="question-label-row"><span className="question-label">INTERVIEW FOLLOW-UPS</span><small>Questions the interviewer can ask next</small></div>
        <div>{question.followUps.map((followUp, index) => <article key={followUp.title}><span>0{index + 1}</span><div><b>{followUp.title}</b><p>{followUp.prompt}</p></div></article>)}</div>
      </section>
    </div>
  );
}

function failure(message: string): ExecutionResult {
  return { passed: false, status: 'Runner unavailable', stdout: '', stderr: '', compileOutput: '', message, time: null, memory: null };
}
